// UserContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { getSasToken, getSasTokenSilent, AUTH_ERROR_EVENT } from '../../services/apiService';

export const UserContext = createContext();

// Refresh token 1 hour before expiry
const TOKEN_REFRESH_BUFFER_MS = 60 * 60 * 1000;

export const UserProvider = ({ children }) => {
    const { accounts, instance, inProgress } = useMsal();
    const [userName, setUserName] = useState('');
    const [userId, setUserId] = useState('');
    const [role, setRole] = useState('');
    const [sasToken, setSasToken] = useState('');
    const [sasTokenExpiry, setSasTokenExpiry] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [authReady, setAuthReady] = useState(false);
    const [sasError, setSasError] = useState(false);

    // Function to refresh the SAS token
    const refreshSasToken = useCallback(async () => {
        try {
            const tokenResult = await getSasTokenSilent();
            setSasToken(tokenResult.sasToken);
            setSasTokenExpiry(tokenResult.expiresOn);
            localStorage.setItem('sasToken', tokenResult.sasToken);
            localStorage.setItem('sasTokenExpiry', tokenResult.expiresOn);
            console.log('SAS token refreshed, expires:', tokenResult.expiresOn);
            return tokenResult;
        } catch (error) {
            console.error('Failed to refresh SAS token:', error);
            throw error;
        }
    }, []);

    // Full auth reset — clears all app state, localStorage, and MSAL active account.
    // Preserves MSAL account cache so logoutRedirect can still work if needed,
    // but clears the active account so bootstrap won't silently rehydrate on reload.
    const resetAuth = useCallback(() => {
        localStorage.removeItem('userName');
        localStorage.removeItem('userId');
        localStorage.removeItem('role');
        localStorage.removeItem('sasToken');
        localStorage.removeItem('sasTokenExpiry');
        instance.setActiveAccount(null);
        setUserName('');
        setUserId('');
        setRole('');
        setSasToken('');
        setSasTokenExpiry('');
        setIsAuthenticated(false);
        setAuthReady(false);
        setSasError(false);
    }, [instance]);

    // Function to check if token needs refresh
    const isTokenExpiringSoon = useCallback(() => {
        if (!sasTokenExpiry) return true;
        const expiryDate = new Date(sasTokenExpiry);
        if (isNaN(expiryDate.getTime())) return true; // invalid date = needs refresh
        const now = new Date();
        const timeUntilExpiry = expiryDate.getTime() - now.getTime();
        return timeUntilExpiry <= TOKEN_REFRESH_BUFFER_MS;
    }, [sasTokenExpiry]);

    // Function to ensure valid token - call this before operations that need the token
    const ensureValidSasToken = useCallback(async () => {
        if (isTokenExpiringSoon()) {
            await refreshSasToken();
        }
        return localStorage.getItem('sasToken');
    }, [isTokenExpiringSoon, refreshSasToken]);

    useEffect(() => {
        // Don't act on accounts while an interaction is still in progress
        if (inProgress !== InteractionStatus.None) return;

        // Load user data and SAS token info from localStorage
        const storedUserName = localStorage.getItem('userName');
        const storedUserId = localStorage.getItem('userId');
        const storedRole = localStorage.getItem('role');
        const storedSasToken = localStorage.getItem('sasToken');
        const storedSasTokenExpiry = localStorage.getItem('sasTokenExpiry');

        if (storedUserName && storedUserId && storedRole && storedSasToken && storedSasTokenExpiry) {
            setUserName(storedUserName);
            setUserId(storedUserId);
            setRole(storedRole);
            setIsAuthenticated(true);

            // Validate stored SAS token before trusting it — use the same buffer
            // as the rest of the refresh logic so bootstrap doesn't admit a token
            // that's about to expire and then immediately need a timer refresh.
            const expiryDate = new Date(storedSasTokenExpiry);
            const isStale = isNaN(expiryDate.getTime()) || (expiryDate.getTime() - Date.now()) <= TOKEN_REFRESH_BUFFER_MS;

            if (isStale) {
                // Stored SAS is stale or expiring soon — refresh before admitting user
                (async () => {
                    try {
                        const tokenResult = await getSasTokenSilent();
                        setSasToken(tokenResult.sasToken);
                        setSasTokenExpiry(tokenResult.expiresOn);
                        localStorage.setItem('sasToken', tokenResult.sasToken);
                        localStorage.setItem('sasTokenExpiry', tokenResult.expiresOn);
                        setAuthReady(true);
                    } catch (error) {
                        console.error('Failed to refresh stale SAS token:', error);
                        setSasError(true);
                    }
                    setLoading(false);
                })();
            } else {
                setSasToken(storedSasToken);
                setSasTokenExpiry(storedSasTokenExpiry);
                setAuthReady(true);
                setLoading(false);
            }
        } else if (accounts.length > 0 && instance.getActiveAccount()) {
            // Fallback if localStorage is empty but there is an active MSAL account.
            // If active account is null (cleared by resetAuth), don't rehydrate —
            // the user intentionally signed out and should go through login again.
            const account = instance.getActiveAccount();
            const defaultRole = account.idTokenClaims?.roles?.[0] || 'Role.User';

            setUserName(account.name);
            setUserId(account.username);
            setRole(defaultRole);
            setIsAuthenticated(true);

            localStorage.setItem('userName', account.name);
            localStorage.setItem('userId', account.username);
            localStorage.setItem('role', defaultRole);

            // Fetch a new SAS token
            (async () => {
                try {
                    const tokenResult = await getSasTokenSilent();
                    setSasToken(tokenResult.sasToken);
                    setSasTokenExpiry(tokenResult.expiresOn);
                    localStorage.setItem('sasToken', tokenResult.sasToken);
                    localStorage.setItem('sasTokenExpiry', tokenResult.expiresOn);
                    setAuthReady(true);
                } catch (error) {
                    console.error('Failed to fetch initial SAS token:', error);
                    setSasError(true);
                }
                setLoading(false);
            })();
        } else {
            setIsAuthenticated(false);
            setAuthReady(false);
            setLoading(false);
        }
    }, [accounts, inProgress]);

    // Check for SAS token expiration and proactively refresh
    useEffect(() => {
        if (!sasTokenExpiry || !isAuthenticated) return;

        let cancelled = false;
        const expiryDate = new Date(sasTokenExpiry);
        if (isNaN(expiryDate.getTime())) {
            console.error('Invalid sasTokenExpiry value, skipping expiration check:', sasTokenExpiry);
            return;
        }
        const now = new Date();
        const timeUntilExpiry = expiryDate.getTime() - now.getTime();

        // If token has already expired, try to refresh silently first
        if (timeUntilExpiry <= 0) {
            (async () => {
                try {
                    const tokenResult = await refreshSasToken();
                    if (!cancelled) {
                        console.log('Expired SAS token refreshed successfully:', tokenResult.expiresOn);
                    }
                    return;
                } catch (error) {
                    console.error('Failed to refresh expired SAS token, logging out:', error);
                }

                if (cancelled) return;

                // Only remove app-specific keys — preserve MSAL's cache so logoutRedirect works
                localStorage.removeItem('userName');
                localStorage.removeItem('userId');
                localStorage.removeItem('role');
                localStorage.removeItem('sasToken');
                localStorage.removeItem('sasTokenExpiry');
                setIsAuthenticated(false);

                try {
                    await instance.logoutRedirect({
                        postLogoutRedirectUri: 'https://oil.spm-automation.com/signin',
                    });
                } catch (logoutError) {
                    console.error('logoutRedirect failed, falling back to manual redirect:', logoutError);
                    window.location.href = '/signin';
                }
            })();
            return () => { cancelled = true; };
        }

        // Schedule a refresh 1 hour before expiry
        const refreshTime = Math.max(timeUntilExpiry - TOKEN_REFRESH_BUFFER_MS, 0);

        const refreshTimer = setTimeout(async () => {
            try {
                await refreshSasToken();
            } catch (error) {
                console.error('Scheduled token refresh failed:', error);
                setSasError(true);
                setAuthReady(false);
            }
        }, refreshTime);

        return () => clearTimeout(refreshTimer);
    }, [sasTokenExpiry, isAuthenticated, instance, refreshSasToken]);

    // Re-check SAS token validity when tab becomes visible (handles sleep/wake, backgrounding)
    useEffect(() => {
        if (!isAuthenticated) return;

        const handleVisibilityChange = async () => {
            if (document.visibilityState !== 'visible') return;
            if (!sasTokenExpiry) return;

            const expiryDate = new Date(sasTokenExpiry);
            if (isNaN(expiryDate.getTime())) {
                console.error('Invalid sasTokenExpiry value:', sasTokenExpiry);
                return;
            }

            const now = new Date();
            const timeUntilExpiry = expiryDate.getTime() - now.getTime();

            if (timeUntilExpiry <= TOKEN_REFRESH_BUFFER_MS) {
                try {
                    await refreshSasToken();
                    console.log('SAS token refreshed on tab focus');
                } catch (error) {
                    console.error('Failed to refresh SAS token on tab focus:', error);
                    setSasError(true);
                    setAuthReady(false);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isAuthenticated, sasTokenExpiry, refreshSasToken]);

    // Listen for backend 401/403 errors and reset auth state
    useEffect(() => {
        const handleAuthError = () => {
            console.error('Backend returned 401/403 — resetting auth state');
            resetAuth();
        };

        window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);
        return () => window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
    }, [resetAuth]);

    return (
        <UserContext.Provider
            value={{
                userName,
                setUserName,
                userId,
                setUserId,
                role,
                setRole,
                sasToken,
                setSasToken,
                sasTokenExpiry,
                setSasTokenExpiry,
                isAuthenticated,
                setIsAuthenticated,
                loading,
                authReady,
                setAuthReady,
                sasError,
                setSasError,
                resetAuth,
                refreshSasToken,
                ensureValidSasToken,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
